import $ from 'jquery';

import FavoriteProductView from 'chairisher/view/product/favorite';
import ProductModel from 'chairisher/model/product';
import RailUtils from 'chairisher/util/rail';
import RepeatingScroller from 'chairisher/component/scroller/repeating';

import bindAddToCart from 'chairisher/view/product/addtocart';

import { trackAddToCart } from 'chairisher/analytics/cart';
import { trackRailLinkClick, trackRailView } from 'chairisher/analytics/components/rail';
import { countryCodes, getPrimaryCountry } from 'chairisher/context/location';
import { updateProductIdToProductObjMap, updateProductObjectArray } from 'chairisher/context/product';
import { ProductRailType } from 'chairisher/util/analytics';
import { isMinMediumDesktop } from 'chairisher/util/mediaquery';
import { observeIntersectionForElsOnce } from 'chairisher/view/helper/intersectionobserver';
import {
    HOUSE_AD_CLASS,
    PROMOTED_LISTING_CLASS,
    registerProductImpression,
} from 'chairisher/view/helper/promotedlistings';

/**
 * The maximum number of products permitted before a rail becomes scrollable
 */
const MAX_NUM_PRODUCTS_FOR_NON_SCROLLABLE_RAIL = 6;

/**
 * The number of products required for the rail's scroll to behave properly
 */
const NUM_PRODUCTS_TO_SCROLL_PROPERLY = 24;

/**
 * @param {string} endpointUrl The endpoint to hit to retrieve the URL. Note: The URL must include grid_type, product_id, and limit
 * @param {Object} options Collection of options to pass the endpoint
 * @param {jQuery} options.$containerEl The element containing the rail
 * @param {jQuery} options.$el The element representing the rail
 * @param {AnalyticsUtils.Tracking.POSITION=} options.analyticsTrackingPosition The position to identify this element's position in various analytics reports
 * @param {AnalyticsUtils.Tracking.SUB_TYPE=} options.analyticsTrackingSubtype The sub-type of the product rail to identify this element in analytics reports
 * @param {AnalyticsUtils.Tracking.TYPE=} options.analyticsTrackingType The type of the product rail to identify this element in analytics reports
 * @param {boolean=} options.canScroll An optional argument to indicate if the rail can scroll, provided it has enough products
 * @param {Array.<number>=} options.excludeIds An optional array of product ids to exclude in order to avoid duplicates
 * @param {boolean=} options.shouldRestrictToShipsTo An optional argument to only display products that can be shipped to the user
 * @param {boolean=} options.shouldShowBadging An optional argument for the product to show badges
 * @param {boolean=} options.shouldShowDetails An optional argument for the product to show details (badges, title, price)
 * @param {string=} options.sort An optional string to indicate a variation to the sort order (e.g. 'alt')
 * @param {number=} options.minNumProductsToDisplayThreshold An optional number to indicate the minimum number of products required to display the rail (defaults to 3)
 *
 */
class ProductRail {
    constructor(
        endpointUrl = null,
        {
            $containerEl = null,
            $el = null,
            analyticsTrackingPosition = null,
            analyticsTrackingSubtype = null,
            analyticsTrackingType = null,
            canScroll = true,
            excludeIds = null,
            shouldRestrictToShipsTo = false,
            shouldShowBadging = true,
            shouldShowDetails = true,
            sort = null,
            minNumProductsToDisplayThreshold = 3,
        } = {},
    ) {
        this.$containerEl = $containerEl;
        this.$el = $el;
        this.analyticsTrackingPosition = analyticsTrackingPosition;
        this.analyticsTrackingSubtype = analyticsTrackingSubtype;
        this.analyticsTrackingType = analyticsTrackingType;
        this.canScroll = canScroll;
        this.endPointUrl = endpointUrl;
        this.excludeIds = excludeIds;
        this.shouldRestrictToShipsTo = shouldRestrictToShipsTo;
        this.shouldShowAddToCartButton = !!$containerEl?.data('should-show-add-to-cart-button');
        this.shouldShowBadging = shouldShowBadging;
        this.shouldShowDetails = shouldShowDetails;
        this.sort = sort;
        this.minNumProductsToDisplayThreshold = minNumProductsToDisplayThreshold;

        this.bindAnalytics();
    }

    /**
     * Fetches data from the server and populates the rail
     *
     * @returns {jqXHR}
     */
    fetchData() {
        if (!this.$el || !this.$el.length) {
            return $.Deferred().reject(
                new Error('Action failed: Unable to fetch product rail data, as element not found.'),
            );
        }

        const data = { is_rail: true };

        if (Array.isArray(this.excludeIds) && this.excludeIds.length) {
            data.exclude_ids = this.excludeIds.join(',');
        }
        if (this.shouldShowAddToCartButton) {
            data.should_show_add_to_cart_button = true;
        }
        if (this.shouldShowDetails) {
            data.should_show_details = true;
        }
        if (this.sort) {
            data.sort = this.sort;
        }
        if (this.shouldShowBadging) {
            data.shouldShowBadging = this.shouldShowBadging;
        }
        if (
            this.shouldRestrictToShipsTo &&
            ![countryCodes.anonymous, countryCodes.unknown].includes(getPrimaryCountry())
        ) {
            data.ships_to = getPrimaryCountry();
        }

        // if rail is loading, set height as soon as fetch is initiated to help prevent layout shift
        if (this.$containerEl.hasClass('loading')) {
            this.setContainerHeight();
        }

        return $.ajax({
            data,
            url: this.endPointUrl,
        }).done((responseData) => {
            // TODO: (CHAIR-14874) update all rail implementations to send down product_id_to_product_json
            let productIdToProductJson = responseData.product_id_to_product_json;
            if (Object.values(productIdToProductJson || {}).length === 0) {
                productIdToProductJson = (responseData.products || []).reduce(
                    (o, p) => Object.assign(o, { [p.id]: p }),
                    {},
                );
            }
            updateProductIdToProductObjMap(productIdToProductJson);
            let products = Object.values(productIdToProductJson);
            let productsHtml = responseData.products_html || [];

            const numProducts = products.length;
            if (numProducts && productsHtml.length) {
                const isRailScrollable = this.canScroll && numProducts > MAX_NUM_PRODUCTS_FOR_NON_SCROLLABLE_RAIL;
                if (isRailScrollable) {
                    products = this.fillOutProductRail(products);
                    productsHtml = this.fillOutProductRail(productsHtml);
                }

                if (responseData.product_grid_title) {
                    this.$containerEl.find('.js-product-grid-title').text(responseData.product_grid_title);
                }

                if (responseData.view_all_url) {
                    const $link = this.$containerEl.find('.js-view-all-link');
                    $link.attr({
                        href: responseData.view_all_url,
                        rel: 'nofollow',
                    });
                }

                this.$el.html(productsHtml);
                this.clearLoadingState();

                // if the rail does not have a minimum product threshold, it can be shown at this stage
                const isMinProductRail = this.$containerEl.hasClass('min-product-rail');
                if (!isMinProductRail) {
                    this.$containerEl.removeClass('retracted');
                }

                updateProductObjectArray(products);

                // Bind add to cart forms
                if (this.shouldShowAddToCartButton) {
                    Array.from(this.$el.find('.js-product')).forEach((productEl) => {
                        const $productEl = $(productEl);
                        const productId = parseInt($productEl.data('product-id'), 10);
                        const productModel = new ProductModel(products.find((product) => product.id === productId));

                        bindAddToCart({
                            $cartForm: $productEl.find('.js-buy-now-form'),
                            onSuccess: (quantity) => {
                                trackAddToCart(productModel, this.analyticsTrackingPosition, quantity);
                            },
                        });
                    });
                }

                if (isRailScrollable) {
                    RepeatingScroller.createProductScroller(this.$el, {
                        analyticsTrackingPosition: this.analyticsTrackingPosition,
                        analyticsTrackingSubtype: this.analyticsTrackingSubtype,
                        analyticsTrackingType: this.analyticsTrackingType,
                        shouldAutoScroll: false,
                    });
                } else {
                    this.setContainerHeight();
                    $(window).smartresize(() => this.setContainerHeight());
                }

                // show rail at this stage if it has a minimum product threshold and it's been reached
                if (isMinProductRail) {
                    this.$containerEl.toggleClass('retracted', numProducts < this.minNumProductsToDisplayThreshold);
                }

                if (responseData.promoted_listing_product_ids?.length > 0) {
                    this.#handlePromotedListings(responseData.promoted_listing_product_ids);
                }

                trackRailView({
                    position: this.analyticsTrackingPosition,
                    subtype: this.analyticsTrackingSubtype,
                    type: this.analyticsTrackingType,
                    count: numProducts,
                });

                FavoriteProductView.syncRailFavorites(this.$containerEl);
            } else {
                this.clearLoadingState();
                this.$containerEl.removeClass('retracted').addClass('hidden');
            }
        });
    }

    /**
     * Binds analytics to the various elements of the rail
     */
    bindAnalytics() {
        this.$containerEl.on('click', '.js-view-all-link', (e) => {
            trackRailLinkClick({
                e,
                type: this.analyticsTrackingType,
            });
        });

        this.$containerEl.on('click', '.js-product-link', (e) => {
            trackRailLinkClick({
                e,
                isProduct: true,
                type: this.analyticsTrackingType,
            });
        });
    }

    /**
     * Duplicates the products in the array until it meets the number of products necessary to ensure proper scroll behavior
     *
     * @param {array} productArray the array of products to be updated; length should be less than NUM_PRODUCTS_TO_SCROLL_PROPERLY
     * @returns {array} the new array; length should now be equal to NUM_PRODUCTS_TO_SCROLL_PROPERLY
     */
    fillOutProductRail(productArray) {
        return Array.from({ length: NUM_PRODUCTS_TO_SCROLL_PROPERLY }, (_, i) => productArray[i % productArray.length]);
    }

    /**
     * @returns {jQuery} The children in this rail
     */
    getChildEls() {
        return this.$el.children('.product');
    }

    #handlePromotedListings(promotedListingProductIds) {
        const $childEls = this.getChildEls();
        $childEls.each((_, el) => {
            const $el = $(el);
            const productId = parseInt($el.data('product-id'), 10);
            if (promotedListingProductIds.includes(productId)) {
                $el.addClass(PROMOTED_LISTING_CLASS);
            } else {
                $el.addClass(HOUSE_AD_CLASS);
            }
        });

        observeIntersectionForElsOnce($childEls, ({ target }) => {
            registerProductImpression($(target), this.analyticsTrackingType, this.analyticsTrackingSubtype);
        });
    }

    /**
     * Clears rail loading state if present; visual and accessibility technologies
     */
    clearLoadingState() {
        if (this.$containerEl.hasClass('loading')) {
            this.$containerEl.removeClass('loading').attr('aria-busy', 'false');
        }
    }

    /**
     * Sets the height of the rail container to the height of the tallest child element
     */
    setContainerHeight() {
        const $children = this.getChildEls();
        if ($children.length > 0) {
            const container = this.$el.get(0);

            if (this.$containerEl.hasClass('dynamic-height-rail')) {
                container.style.height = RailUtils.calculateContainerHeight({ $children, container });
            } else {
                container.style.height = `${$children.get(0).offsetHeight}px`;
            }
        }
    }
}

/**
 * Initializes the New Arrivals rail if present
 */
export function initNewArrivalsRail() {
    const $container = $('#js-new-arrival-grid');
    if ($container.length) {
        RepeatingScroller.createProductScroller($container, {
            analyticsTrackingType: ProductRailType.NEW_ARRIVALS,
            shouldAutoScroll: isMinMediumDesktop(),
        });
    }
}

export default ProductRail;
